import { useRef } from "react";
import {
  ArrowRight,
  Bell,
  Bookmark,
  Check,
  CheckCheck,
  ChevronDown,
  ClipboardCheck,
  Download,
  FileCode2,
  FileSpreadsheet,
  FileText,
  LayoutGrid,
  ListTodo,
  MessageCircleMore,
  MonitorCheck,
  MoreVertical,
  Palette,
  Paperclip,
  Phone,
  Plus,
  Presentation,
  Search,
  Send,
  ShieldCheck,
  Smile,
  StickyNote,
  UsersRound,
  Video,
  Zap,
  PlugZap,
} from "lucide-react";
import Reveal from "./Reveal";

const features = [
  {
    title: "Real-Time Messaging",
    copy: "Communicate instantly through direct messages, team channels, voice calls, and file sharing.",
    Icon: MessageCircleMore,
    accent: "purple",
  },
  {
    title: "Smart Work Management",
    copy: "Assign tasks, track progress, and keep every project moving with complete visibility.",
    Icon: ClipboardCheck,
    accent: "green",
  },
  {
    title: "Remote Support",
    copy: "Connect securely to devices, troubleshoot issues, and resolve problems without leaving the platform.",
    Icon: MonitorCheck,
    accent: "blue",
  },
  {
    title: "Built-in Productivity Tools",
    copy: "Stay organized with reminders, sticky notes, bookmarks, and everyday work essentials.",
    Icon: LayoutGrid,
    accent: "gold",
  },
];

const benefitCards = [
  {
    title: "Secure & Reliable",
    copy: "Enterprise-grade security and 99.9% uptime to keep your work safe.",
    Icon: ShieldCheck,
    accent: "blue",
  },
  {
    title: "Fast & Lightweight",
    copy: "Built for speed so your team can stay productive, always.",
    Icon: Zap,
    accent: "green",
  },
  {
    title: "Made for Teams",
    copy: "Everything your team needs to collaborate and win together.",
    Icon: UsersRound,
    accent: "purple",
  },
  {
    title: "Seamless Integration",
    copy: "Works with the tools you use every day.",
    Icon: PlugZap,
    accent: "gold",
  },
];
const workspaceChats = [
  {
    name: "Emily Carter",
    preview: "Sounds good! I'll review and get back...",
    time: "10:50 AM",
    unread: 2,
    avatar: "https://i.pravatar.cc/100?img=47",
    active: true,
  },
  {
    name: "Michael Reed",
    preview: "Can you share the latest numbers?",
    time: "10:32 AM",
    unread: 1,
    avatar: "https://i.pravatar.cc/100?img=12",
  },
  {
    name: "Olivia Bennett",
    preview: "Thanks! That helps a lot.",
    time: "9:48 AM",
    avatar: "https://i.pravatar.cc/100?img=32",
  },
  {
    name: "Daniel Brooks",
    preview: "Let's sync up later this afternoon.",
    time: "Yesterday",
    avatar: "https://i.pravatar.cc/100?img=11",
  },
  {
    name: "Sophia Turner",
    preview: "I've updated the document.",
    time: "Yesterday",
    avatar: "https://i.pravatar.cc/100?img=44",
  },
  {
    name: "Ethan Collins",
    preview: "Please confirm the meeting time.",
    time: "Jul 3",
    avatar: "https://i.pravatar.cc/100?img=13",
  },
  {
    name: "Ava Murphy",
    preview: "Great work on the presentation!",
    time: "Jul 3",
    avatar: "https://i.pravatar.cc/100?img=49",
  },
];

const workspaceFiles = [
  {
    name: "Project_Overview.pdf",
    meta: "PDF • 2.4 MB",
    type: "pdf",
    Icon: FileText,
  },
  {
    name: "Budget_Tracker.xlsx",
    meta: "XLSX • 1.1 MB",
    type: "excel",
    Icon: FileSpreadsheet,
  },
  {
    name: "Timeline_2026.pptx",
    meta: "PPTX • 3.7 MB",
    type: "powerpoint",
    Icon: Presentation,
  },
];

const workspaceReminders = [
  {
    title: "Team stand-up meeting",
    date: "Today, 11:00 AM",
  },
  {
    title: "Review Q2 budget report",
    date: "Tomorrow, 2:00 PM",
  },
  {
    title: "Client presentation prep",
    date: "Jul 6, 9:30 AM",
  },
];

const workspaceTasks = [
  {
    title: "Prepare Q3 roadmap",
    date: "Jul 7",
    tone: "danger",
  },
  {
    title: "Update project timeline",
    date: "Jul 5",
    tone: "warning",
  },
  {
    title: "Share weekly status report",
    date: "Today",
    tone: "success",
  },
];
function WorkspacePreview() {
  const ref = useRef(null);

  const handlePointerMove = (event) => {
    const node = ref.current;
    if (!node) return;

    const rect = node.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const rotateY = (x / rect.width - 0.5) * 2.2;
    const rotateX = (y / rect.height - 0.5) * -2.2;

    node.style.setProperty(
      "--workspace-tilt-x",
      `${rotateX.toFixed(2)}deg`
    );

    node.style.setProperty(
      "--workspace-tilt-y",
      `${rotateY.toFixed(2)}deg`
    );

    node.style.setProperty("--workspace-glow-x", `${x}px`);
    node.style.setProperty("--workspace-glow-y", `${y}px`);
  };

  const resetPointer = () => {
    const node = ref.current;
    if (!node) return;

    node.style.setProperty("--workspace-tilt-x", "0deg");
    node.style.setProperty("--workspace-tilt-y", "0deg");
  };

  return (
    <div
      ref={ref}
      className="workspace-reference-stage"
      onPointerMove={handlePointerMove}
      onPointerLeave={resetPointer}
    >
      <div
        className="workspace-reference-glow"
        aria-hidden="true"
      />

      <div className="workspace-css-app">
        {/* Left application navigation */}
        <aside className="workspace-app-nav">
          <div className="workspace-mini-logo">
            <span>C</span>
          </div>

          <div className="workspace-nav-buttons">
            <button
              type="button"
              className="is-active"
              aria-label="Messages"
            >
              <MessageCircleMore size={17} />
            </button>

            <button type="button" aria-label="People">
              <UsersRound size={17} />
            </button>

            <button type="button" aria-label="Workspace">
              <LayoutGrid size={17} />
            </button>

            <button type="button" aria-label="Tasks">
              <ClipboardCheck size={17} />
            </button>

            <button type="button" aria-label="Bookmarks">
              <Bookmark size={17} />
            </button>
          </div>

          <div className="workspace-nav-profile">
            <img
              src="https://i.pravatar.cc/100?img=14"
              alt="Harshit Degul"
            />
            <span />
          </div>
        </aside>

        {/* Chat sidebar */}
        <aside className="workspace-chat-sidebar">
          <header className="workspace-account-header">
            <div className="workspace-account-person">
              <div className="workspace-avatar-wrap">
                <img
                  src="https://i.pravatar.cc/100?img=14"
                  alt="Harshit Degul"
                />
                <span />
              </div>

              <div>
                <strong>Harshit Degul</strong>
                <small>My Account</small>
              </div>
            </div>

            <button type="button" aria-label="Account menu">
              <MoreVertical size={17} />
            </button>
          </header>

          <div className="workspace-chat-search">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search chats..."
              aria-label="Search chats"
            />
            <button type="button" aria-label="Search filters">
              <LayoutGrid size={14} />
            </button>
          </div>

          <div className="workspace-chat-list">
            {workspaceChats.map((chat, index) => (
              <button
                type="button"
                className={`workspace-chat-row ${
                  chat.active ? "is-active" : ""
                }`}
                key={chat.name}
                style={{
                  "--chat-delay": `${index * 90}ms`,
                }}
              >
                <div className="workspace-avatar-wrap">
                  <img src={chat.avatar} alt={chat.name} />
                  <span />
                </div>

                <div className="workspace-chat-copy">
                  <div className="workspace-chat-name-row">
                    <strong>{chat.name}</strong>
                    <time>{chat.time}</time>
                  </div>

                  <div className="workspace-chat-preview-row">
                    <small>{chat.preview}</small>

                    {chat.unread ? (
                      <span className="workspace-unread-badge">
                        {chat.unread}
                      </span>
                    ) : null}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </aside>

        {/* Main chat */}
        <main className="workspace-conversation">
          <header className="workspace-conversation-header">
            <div className="workspace-active-contact">
              <div className="workspace-avatar-wrap">
                <img
                  src="https://i.pravatar.cc/100?img=47"
                  alt="Emily Carter"
                />
                <span />
              </div>

              <div>
                <strong>Emily Carter</strong>
                <small>
                  <i /> Online
                </small>
              </div>
            </div>

            <div className="workspace-header-actions">
              <button type="button" aria-label="Start phone call">
                <Phone size={16} />
              </button>

              <button type="button" aria-label="Start video call">
                <Video size={17} />
              </button>

              <button type="button" aria-label="Search messages">
                <Search size={17} />
              </button>

              <button type="button" aria-label="More options">
                <MoreVertical size={17} />
              </button>
            </div>
          </header>

          <div className="workspace-message-area">
            <div className="workspace-chat-date">
              <span>Today, July 3, 2026</span>
            </div>

            <div className="workspace-message workspace-message--received workspace-message--one">
              <img
                src="https://i.pravatar.cc/100?img=47"
                alt=""
              />

              <div className="workspace-message-content">
                <div className="workspace-message-bubble">
                  Hey Harshit! Can you share the latest project files
                  when you get a chance?
                </div>
                <time>10:47 AM</time>
              </div>
            </div>

            <div className="workspace-message workspace-message--sent workspace-message--two">
              <div className="workspace-message-content">
                <div className="workspace-message-bubble">
                  Absolutely, here you go.
                </div>

                <div className="workspace-sent-meta">
                  <time>10:48 AM</time>
                  <CheckCheck size={14} />
                </div>
              </div>
            </div>

            <div className="workspace-file-stack">
              {workspaceFiles.map((file, index) => {
                const Icon = file.Icon;

                return (
                  <div
                    className={`workspace-file-card workspace-file-card--${file.type}`}
                    style={{
                      "--file-delay": `${550 + index * 140}ms`,
                    }}
                    key={file.name}
                  >
                    <div className="workspace-file-information">
                      <div className="workspace-file-icon">
                        <Icon size={18} />
                      </div>

                      <div>
                        <strong>{file.name}</strong>
                        <small>{file.meta}</small>
                      </div>
                    </div>

                    <button type="button">
                      <Download size={14} />
                      <span>Download</span>
                    </button>
                  </div>
                );
              })}

              <div className="workspace-file-time">
                <time>10:48 AM</time>
                <CheckCheck size={13} />
              </div>
            </div>

            <div className="workspace-message workspace-message--received workspace-message--three">
              <img
                src="https://i.pravatar.cc/100?img=47"
                alt=""
              />

              <div className="workspace-message-content">
                <div className="workspace-message-bubble">
                  Thanks! I'll review these and get back to you.
                </div>
                <time>10:49 AM</time>
              </div>
            </div>

            <div className="workspace-message workspace-message--sent workspace-message--four">
              <div className="workspace-message-content">
                <div className="workspace-message-bubble">
                  Great! Let me know if you need anything else.
                </div>

                <div className="workspace-sent-meta">
                  <time>10:50 AM</time>
                  <CheckCheck size={14} />
                </div>
              </div>
            </div>

            <div className="workspace-message workspace-message--received workspace-message--five">
              <img
                src="https://i.pravatar.cc/100?img=47"
                alt=""
              />

              <div className="workspace-message-content">
                <div className="workspace-message-bubble">
                  Sounds good! I'll review and get back shortly.
                </div>

                <time>10:50 AM</time>

                <span className="workspace-message-reaction">
                  👍 1
                </span>
              </div>
            </div>

            <div className="workspace-typing">
              <img
                src="https://i.pravatar.cc/100?img=47"
                alt=""
              />

              <span>Emily is typing</span>

              <i />
              <i />
              <i />
            </div>
          </div>

          <footer className="workspace-message-composer">
            <button type="button" aria-label="Attach a file">
              <Paperclip size={17} />
            </button>

            <input
              type="text"
              placeholder="Type a message..."
              aria-label="Message"
            />

            <button type="button" aria-label="Choose emoji">
              <Smile size={18} />
            </button>

            <button
              type="button"
              className="workspace-send-button"
              aria-label="Send message"
            >
              <Send size={17} />
            </button>
          </footer>
        </main>

        {/* My Space panel */}
        <aside className="workspace-productivity-panel">
          <header className="workspace-panel-heading">
            <div>
              <small>Personal workspace</small>
              <h3>My Space</h3>
              <p>
                Your hub for focus, planning and productivity.
              </p>
            </div>

            <span className="workspace-active-count">
              <strong>2</strong>
              Active
            </span>
          </header>

          <div className="workspace-panel-stats">
            <div>
              <Bell size={15} />
              <strong>3</strong>
              <small>Reminders</small>
            </div>

            <div>
              <Check size={15} />
              <strong>5</strong>
              <small>Tasks</small>
            </div>

            <div>
              <StickyNote size={15} />
              <strong>2</strong>
              <small>Notes</small>
            </div>

            <div>
              <Bookmark size={15} />
              <strong>4</strong>
              <small>Bookmarks</small>
            </div>
          </div>

          <section className="workspace-widget workspace-widget--reminders">
            <div className="workspace-widget-heading">
              <div>
                <Bell size={16} />
                <strong>Reminders</strong>
              </div>

              <button type="button" aria-label="Add reminder">
                <Plus size={15} />
              </button>
            </div>

            <div className="workspace-reminder-list">
              {workspaceReminders.map((reminder) => (
                <div key={reminder.title}>
                  <span className="workspace-empty-check" />

                  <div>
                    <strong>{reminder.title}</strong>
                    <small>{reminder.date}</small>
                  </div>
                </div>
              ))}
            </div>

            <a href="#top">View all</a>
          </section>

          <section className="workspace-widget workspace-widget--notes">
            <div className="workspace-widget-heading">
              <div>
                <StickyNote size={16} />
                <strong>Sticky Notes</strong>
              </div>

              <button type="button" aria-label="Add note">
                <Plus size={15} />
              </button>
            </div>

            <div className="workspace-note-grid">
              <article className="workspace-note workspace-note--yellow">
                <strong>Design System Update</strong>
                <p>
                  Update component library with new brand colors and
                  typography.
                </p>
                <small>Today</small>
              </article>

              <article className="workspace-note workspace-note--green">
                <strong>Client Feedback</strong>
                <p>
                  Add the latest review feedback into the next
                  iteration.
                </p>
                <small>Yesterday</small>
              </article>
            </div>
          </section>

          <section className="workspace-widget workspace-widget--tasks">
            <div className="workspace-widget-heading">
              <div>
                <ListTodo size={17} />
                <strong>To-do List</strong>
              </div>

              <button type="button" aria-label="Add task">
                <Plus size={15} />
              </button>
            </div>

            <div className="workspace-task-list">
              {workspaceTasks.map((task) => (
                <div key={task.title}>
                  <span className="workspace-empty-check" />
                  <strong>{task.title}</strong>

                  <small
                    className={`workspace-task-date workspace-task-date--${task.tone}`}
                  >
                    {task.date}
                  </small>
                </div>
              ))}
            </div>

            <a href="#top">View all</a>
          </section>

          <section className="workspace-widget workspace-widget--bookmarks">
            <div className="workspace-widget-heading">
              <div>
                <Bookmark size={16} />
                <strong>Bookmarks</strong>
              </div>

              <button type="button" aria-label="Add bookmark">
                <Plus size={15} />
              </button>
            </div>

            <div className="workspace-bookmark-list">
              <div>
                <span className="workspace-bookmark-icon">
                  <FileText size={14} />
                </span>

                <div>
                  <strong>Product Roadmap 2026</strong>
                  <small>docs.cullath.com/roadmap</small>
                </div>

                <ArrowRight size={13} />
              </div>

              <div>
                <span className="workspace-bookmark-icon workspace-bookmark-icon--green">
                  <FileCode2 size={14} />
                </span>

                <div>
                  <strong>Company OKRs</strong>
                  <small>drive.cullath.com/okrs</small>
                </div>

                <ArrowRight size={13} />
              </div>
            </div>
          </section>

          <section className="workspace-widget workspace-widget--appearance">
            <div className="workspace-widget-heading">
              <div>
                <Palette size={16} />
                <strong>Appearance</strong>
              </div>

              <ChevronDown size={15} />
            </div>

            <div className="workspace-theme-options">
              <button type="button" className="is-active">
                <span>☀</span>
                Light
              </button>

              <button type="button">
                <span>☁</span>
                Soft
              </button>

              <button type="button">
                <span>☾</span>
                Dark
              </button>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}

export default function WorkspaceShowcaseSection() {
  return (
    <section className="workspace-showcase-section" aria-labelledby="workspace-showcase-title">
      <div className="workspace-showcase-haze workspace-showcase-haze--one" aria-hidden="true" />
      <div className="workspace-showcase-haze workspace-showcase-haze--two" aria-hidden="true" />
      <div className="workspace-showcase-grid-dots" aria-hidden="true" />

      <div className="page-container workspace-showcase-container">
        <Reveal className="workspace-showcase-heading workspace-showcase-heading--center">
          <div className="workspace-showcase-kicker">
            <i aria-hidden="true" /> EVERYTHING IN ONE PLACE
          </div>
          <h2 id="workspace-showcase-title">
            Everything Your Team Needs—
            <span>In One Place.</span>
          </h2>
          <p>
            Cullath combines team messaging, work management, remote support, and built-in productivity tools into one connected workspace. Keep conversations, tasks, files, and daily work together—without switching between multiple apps.
          </p>
        </Reveal>

        <div className="workspace-showcase-body">
          <div className="workspace-feature-rail">
            {features.map((feature, index) => {
              const Icon = feature.Icon;
              return (
                <Reveal
                  className={`workspace-feature-item workspace-feature-item--${feature.accent}`}
                  delay={80 + index * 110}
                  key={feature.title}
                >
                  <div className="workspace-feature-icon">
                    <Icon size={25} strokeWidth={1.95} />
                  </div>
                  <div className="workspace-feature-copy">
                    <h3>{feature.title}</h3>
                    <p>{feature.copy}</p>
                  </div>
                </Reveal>
              );
            })}

          </div>

          <Reveal className="workspace-main-stage" delay={140}>
            <div className="workspace-orbit-lines" aria-hidden="true">
              <span className="workspace-orbit workspace-orbit--one" />
              <span className="workspace-orbit workspace-orbit--two" />
              <span className="workspace-orbit workspace-orbit--three" />
              <span className="workspace-orbit-node workspace-orbit-node--one" />
              <span className="workspace-orbit-node workspace-orbit-node--two" />
              <span className="workspace-orbit-node workspace-orbit-node--three" />
              <span className="workspace-orbit-node workspace-orbit-node--four" />
            </div>

            <div className="workspace-floating-signal workspace-floating-signal--message" aria-hidden="true">
              <i /> <strong>Message</strong><small>Stay connected</small>
            </div>
            <div className="workspace-floating-signal workspace-floating-signal--support" aria-hidden="true">
              <i /> <strong>Support</strong><small>Resolve faster</small>
            </div>
            <div className="workspace-floating-signal workspace-floating-signal--manage" aria-hidden="true">
              <i /> <strong>Manage</strong><small>Track & organize</small>
            </div>
            <div className="workspace-floating-signal workspace-floating-signal--productivity" aria-hidden="true">
              <i /> <strong>Productivity</strong><small>Work efficiently</small>
            </div>

            <WorkspacePreview />
          </Reveal>
        </div>

        <div className="workspace-benefit-row">
          {benefitCards.map((card, index) => {
            const Icon = card.Icon;
            return (
              <Reveal
                className={`workspace-benefit-card workspace-benefit-card--${card.accent}`}
                delay={100 + index * 100}
                key={card.title}
              >
                <div className="workspace-benefit-icon">
                  <Icon size={24} strokeWidth={1.9} />
                </div>
                <div className="workspace-benefit-copy">
                  <strong>{card.title}</strong>
                  <p>{card.copy}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="workspace-showcase-footer-note" delay={180}>
          One platform. All the tools. <span>Zero distractions.</span>
        </Reveal>
      </div>
    </section>
  );
}
